export type ParsedTraderRow = { email?: string; userId?: string; username?: string };

export type ParseTraderCsvResult =
  | { ok: true; rows: ParsedTraderRow[] }
  | { ok: false; error: string };

const EMAIL_HEADER = 'email';
const USER_ID_HEADER = 'user_id';
const USERNAME_HEADERS = ['username', 'user_name', 'screen_name', 'screenname'];

const unquote = (cell: string): string => {
  const first = cell[0];
  if (cell.length >= 2 && (first === '"' || first === "'") && cell.at(-1) === first) {
    return cell.slice(1, -1);
  }
  return cell;
};

const cells = (line: string): string[] => line.split(',').map((cell) => unquote(cell.trim()));

const parseRow = (
  line: string,
  emailIndex: number,
  userIdIndex: number,
  usernameIndex: number,
): ParsedTraderRow | null => {
  const row = cells(line);
  const email = emailIndex === -1 ? '' : (row[emailIndex] ?? '');
  const userId = userIdIndex === -1 ? '' : (row[userIdIndex] ?? '');
  const username = usernameIndex === -1 ? '' : (row[usernameIndex] ?? '');
  if (!email && !userId && !username) return null;

  return {
    ...(email ? { email } : {}),
    ...(userId ? { userId } : {}),
    ...(username ? { username } : {}),
  };
};

const collectRows = (
  lines: string[],
  startIndex: number,
  emailIndex: number,
  userIdIndex: number,
  usernameIndex: number,
): ParsedTraderRow[] => {
  return lines
    .slice(startIndex)
    .filter((line) => line.length > 0)
    .map((line) => parseRow(line, emailIndex, userIdIndex, usernameIndex))
    .filter((row): row is ParsedTraderRow => row !== null);
};

export const parseTraderCsv = (text: string): ParseTraderCsvResult => {
  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim());

  const headerIndex = lines.findIndex((line) => line.length > 0);
  if (headerIndex === -1) return { ok: false, error: 'The CSV is empty.' };

  const header = cells(lines[headerIndex]).map((cell) => cell.toLowerCase());
  const emailIndex = header.indexOf(EMAIL_HEADER);
  const userIdIndex = header.indexOf(USER_ID_HEADER);
  const usernameIndex = header.findIndex((cell) => USERNAME_HEADERS.includes(cell));
  if (emailIndex === -1 && userIdIndex === -1 && usernameIndex === -1) {
    return {
      ok: false,
      error: 'The CSV must have an "email", "user_id" or "username" column.',
    };
  }

  const rows = collectRows(lines, headerIndex + 1, emailIndex, userIdIndex, usernameIndex);
  if (rows.length === 0) return { ok: false, error: 'No traders found in the CSV.' };

  return { ok: true, rows };
};
